/**
 * Resolve the browser-facing API base URL.
 *
 * Prefer same-origin `/api` (proxied by the dev server or static-server) so
 * HTTP-only auth cookies are first-party and survive login. Set
 * REACT_APP_BACKEND_URL only for legacy direct cross-origin API access.
 */
export const resolveApiBaseUrl = (backendUrl = process.env.REACT_APP_BACKEND_URL) => {
  const trimmed = (backendUrl || "").trim().replace(/\/$/, "");
  return trimmed ? `${trimmed}/api` : "/api";
};

export const resolveBackendOrigin = (
  backendUrl = process.env.REACT_APP_BACKEND_URL,
  proxyUrl = process.env.BACKEND_PROXY_URL
) => {
  const candidate = (proxyUrl || backendUrl || "http://localhost:8001").trim();
  return candidate.replace(/\/$/, "");
};

/**
 * Resolve a stored upload URL for use in <img src>.
 *
 * Uploaded assets are stored as `/api/uploads/...`. Thumbnail GET requests are
 * always served through the same-origin `/api` proxy (dev server or static
 * server) so they keep working when JSON API calls use REACT_APP_BACKEND_URL
 * for direct cross-origin access.
 */
export const resolveUploadUrl = (
  url,
  backendUrl = process.env.REACT_APP_BACKEND_URL
) => {
  const trimmed = (url || "").trim();
  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  const normalizedThumbnailPath = trimmed.startsWith("/api/uploads/thumbnails/")
    ? trimmed
    : trimmed.startsWith("/uploads/thumbnails/")
      ? `/api${trimmed}`
      : trimmed.startsWith("uploads/thumbnails/")
        ? `/api/${trimmed}`
        : trimmed;

  if (normalizedThumbnailPath.startsWith("/api/uploads/thumbnails/")) {
    return normalizedThumbnailPath;
  }

  if (normalizedThumbnailPath.startsWith("/api/")) {
    const backend = (backendUrl || "").trim().replace(/\/$/, "");
    return backend ? `${backend}${normalizedThumbnailPath}` : normalizedThumbnailPath;
  }

  return normalizedThumbnailPath;
};

/**
 * Build an authenticated download URL for a course material.
 * Uses the same-origin `/api` proxy so session cookies are sent.
 */
export const buildMaterialDownloadUrl = (
  courseId,
  material,
  backendUrl = process.env.REACT_APP_BACKEND_URL
) => {
  const params = new URLSearchParams({
    course_id: courseId,
    filename: material.filename || "",
    name: material.name || "download",
  });
  return `${resolveApiBaseUrl(backendUrl)}/materials/download?${params}`;
};

/**
 * Secondary URL for thumbnail image requests.
 *
 * Thumbnails are loaded same-origin via `/api/...` first. When a backend origin
 * is configured, this helper returns a direct backend URL fallback for
 * environments where the frontend proxy is unavailable.
 */
export const resolveUploadFallbackUrl = (
  url,
  backendUrl = process.env.REACT_APP_BACKEND_URL
) => {
  const trimmed = (url || "").trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return "";
  const normalizedThumbnailPath = trimmed.startsWith("/api/uploads/thumbnails/")
    ? trimmed
    : trimmed.startsWith("/uploads/thumbnails/")
      ? `/api${trimmed}`
      : trimmed.startsWith("uploads/thumbnails/")
        ? `/api/${trimmed}`
        : trimmed;
  if (!normalizedThumbnailPath.startsWith("/api/uploads/thumbnails/")) return "";

  const backend = (backendUrl || "").trim().replace(/\/$/, "");
  return backend ? `${backend}${normalizedThumbnailPath}` : "";
};
